function norm(val) {
  if (val === null || val === undefined) return "";
  if (Array.isArray(val)) return JSON.stringify(val.map((item) => norm(item)));
  if (typeof val === "object") return JSON.stringify(val);
  return String(val).trim();
}

function changed(before, after) {
  return norm(before) !== norm(after);
}

function isNonEmptyObject(obj) {
  if (!obj || typeof obj !== "object") return false;
  return Object.values(obj).some((v) => norm(v) !== "");
}

export function getEditorName(userInfo, getUserInfoData) {
  const info = userInfo || (getUserInfoData ? getUserInfoData() : null);
  return info ? `${info.firstName} ${info.lastName}` : "Unknown User";
}

export function getEditorUID(userInfo, getUserInfoData) {
  const info = userInfo || (getUserInfoData ? getUserInfoData() : null);
  return info?.uid || null;
}

const PLAYER_PROFILE_LABELS = {
  FirstName: "First name",
  LastName: "Last name",
  hometown_City: "Hometown city",
  hometown_State_Country: "Hometown state/country",
  Team: "Team",
  intlPlayer: "International player",
  Country: "Country",
  Year: "Class year",
  DOB: "Date of birth",
  Position: "Position",
  Hand: "Hand",
  Height: "Height",
  Weight: "Weight",
  Wingspan: "Wingspan",
  Twitter: "Twitter",
  Instagram: "Instagram",
  Snapchat: "Snapchat",
  TikTok: "TikTok",
  aauTeam: "AAU team",
  aauAffil: "AAU affiliation",
  aauCoach: "AAU coach first name",
  aauCoachLast: "AAU coach last name",
  aauCoachInfo: "AAU coach info",
  aauCoachEmail: "AAU coach email",
  Status: "Recruitment status",
  committedTeam: "Committed team",
  Prlow: "Projection low",
  Prhigh: "Projection high",
  rivalsRankingUrl: "Rivals ranking URL",
  ranking247Url: "247 ranking URL",
  on3RankingUrl: "On3 ranking URL",
  espnRankingUrl: "ESPN ranking URL",
};

const PROFILE_SKIP_FIELDS = new Set([
  "Age",
  "Title",
  "Hometown",
  "pRange",
  "id",
  "Headshot",
  "createdAt",
  "dateCreated",
]);

function diffStringList(beforeList = [], afterList = [], singularLabel, pluralLabel) {
  const beforeItems = (beforeList || []).map((item) => norm(item)).filter(Boolean);
  const afterItems = (afterList || []).map((item) => norm(item)).filter(Boolean);
  const added = afterItems.filter((item) => !beforeItems.includes(item));
  const removed = beforeItems.filter((item) => !afterItems.includes(item));
  const details = [];

  if (added.length === 1) {
    details.push(`New ${singularLabel} was added: ${added[0]}`);
  } else if (added.length > 1) {
    details.push(`${added.length} new ${pluralLabel} were added`);
  }

  if (removed.length === 1) {
    details.push(`${singularLabel} was removed: ${removed[0]}`);
  } else if (removed.length > 1) {
    details.push(`${removed.length} ${pluralLabel} were removed`);
  }

  return details;
}

function contactLabel(contact = {}) {
  return [contact.relation, contact.fullName].filter(Boolean).join(" - ") || "Contact";
}

export function diffPlayerProfile(before = {}, after = {}) {
  const details = [];

  Object.entries(PLAYER_PROFILE_LABELS).forEach(([key, label]) => {
    if (!PROFILE_SKIP_FIELDS.has(key) && changed(before[key], after[key])) {
      details.push(`${label} was updated`);
    }
  });

  if (changed(before.viewing_privileges, after.viewing_privileges)) {
    details.push("Viewing privileges were updated");
  }

  for (let i = 1; i <= 20; i += 1) {
    const key = `video${i}`;
    if (changed(before[key], after[key])) {
      details.push(`Video ${i} was updated`);
    }
  }

  return details;
}

export function describeNewPlayerProfile(values = {}) {
  const details = ["New player profile was created"];
  Object.entries(PLAYER_PROFILE_LABELS).forEach(([key, label]) => {
    if (norm(values[key])) {
      details.push(`${label} was set`);
    }
  });
  return details;
}

export function diffPlayerContactAdded(contact = {}) {
  const label = contactLabel(contact);
  const extras = [];
  if (norm(contact.cell)) extras.push("phone");
  if (norm(contact.email)) extras.push("email");
  if (norm(contact.notes)) extras.push("notes");

  if (extras.length > 0) {
    return [`${label} contact was added (${extras.join(", ")})`];
  }
  return [`${label} contact was added`];
}

export function diffPlayerContactEdited(before = {}, after = {}) {
  const label = contactLabel(after.relation ? after : before);
  const details = [];

  if (changed(before.relation, after.relation)) {
    details.push(`${label}: relation was updated`);
  }
  if (changed(before.fullName, after.fullName)) {
    details.push(`Contact name was updated to ${after.fullName}`);
  }
  if (changed(before.cell, after.cell)) {
    details.push(`${label}: phone was updated`);
  }
  if (changed(before.email, after.email)) {
    details.push(`${label}: email was updated`);
  }
  if (changed(before.notes, after.notes)) {
    details.push(`${label}: notes were updated`);
  }

  return details.length ? details : [`${label} contact was edited`];
}

export function diffPlayerContactDeleted(contact = {}) {
  return [`${contactLabel(contact)} contact was deleted`];
}

function diffTeamHistory(before = [], after = []) {
  const beforeEntries = (before || []).filter(isNonEmptyObject);
  const afterEntries = (after || []).filter(isNonEmptyObject);
  const details = [];

  const addedCount = Math.max(0, afterEntries.length - beforeEntries.length);
  const removedCount = Math.max(0, beforeEntries.length - afterEntries.length);

  if (addedCount === 1) details.push("A team history entry was added");
  else if (addedCount > 1) details.push(`${addedCount} team history entries were added`);

  if (removedCount === 1) details.push("A team history entry was removed");
  else if (removedCount > 1) details.push(`${removedCount} team history entries were removed`);

  const sharedCount = Math.min(beforeEntries.length, afterEntries.length);
  for (let i = 0; i < sharedCount; i += 1) {
    const prev = beforeEntries[i];
    const next = afterEntries[i];
    if (changed(prev.school || prev.team, next.school || next.team)) {
      details.push(`Team history entry ${i + 1}: school/team was updated`);
    }
    if (changed(prev.phone, next.phone)) {
      details.push(`Team history entry ${i + 1}: coach phone was updated`);
    }
    if (changed(prev.email, next.email)) {
      details.push(`Team history entry ${i + 1}: coach email was updated`);
    }
    if (changed(prev.coachFirstName, next.coachFirstName) || changed(prev.coachLastName, next.coachLastName)) {
      details.push(`Team history entry ${i + 1}: coach name was updated`);
    }
  }

  return details;
}

export function describeNewBackgroundOverview(values = {}) {
  const details = ["New background overview was created"];
  const teamCount = (values.teamHistory || []).filter(isNonEmptyObject).length;
  if (teamCount > 0) {
    details.push(`${teamCount} team histor${teamCount === 1 ? "y entry" : "y entries"} added`);
  }

  const interestCount = (values.recruitment?.notableInterests || []).filter((item) => norm(item)).length;
  if (interestCount > 0) {
    details.push(`${interestCount} recruitment interest${interestCount === 1 ? "" : "s"} added`);
  }

  const nuggetCount = (values.personal?.nuggets || []).filter((item) => norm(item)).length;
  if (nuggetCount > 0) {
    details.push(`${nuggetCount} family nugget${nuggetCount === 1 ? "" : "s"} added`);
  }

  if (norm(values.cheatSheet?.nuggets)) {
    details.push("Cheat sheet nuggets were added");
  }

  return details;
}

export function diffBackgroundOverview(before = {}, after = {}) {
  const details = [];

  if (changed(before.additionalNotes, after.additionalNotes)) {
    details.push("Additional notes were updated");
  }
  if (changed(before.drafted, after.drafted)) {
    details.push("Drafted status was updated");
  }
  if (changed(before.IBO, after.IBO)) {
    details.push("IBO status was updated");
  }
  if (changed(before.nationalTeamExperience?.country, after.nationalTeamExperience?.country)) {
    details.push("National team country was updated");
  }

  details.push(...diffTeamHistory(before.teamHistory, after.teamHistory));

  details.push(
    ...diffStringList(
      before.recruitment?.notableInterests,
      after.recruitment?.notableInterests,
      "interest",
      "interests"
    )
  );
  details.push(
    ...diffStringList(
      before.recruitment?.notableOffers,
      after.recruitment?.notableOffers,
      "offer",
      "offers"
    )
  );
  details.push(
    ...diffStringList(
      before.recruitment?.notableVisits,
      after.recruitment?.notableVisits,
      "visit",
      "visits"
    )
  );

  if (changed(before.recruitment?.commitment, after.recruitment?.commitment)) {
    details.push("Recruitment commitment was updated");
  }
  if (changed(before.recruitment?.notes, after.recruitment?.notes)) {
    details.push("Recruitment notes were updated");
  }

  details.push(
    ...diffStringList(before.personal?.nuggets, after.personal?.nuggets, "family nugget", "family nuggets")
  );

  const beforeFamily = (before.personal?.familyCircleMembers || []).filter(isNonEmptyObject);
  const afterFamily = (after.personal?.familyCircleMembers || []).filter(isNonEmptyObject);
  if (afterFamily.length > beforeFamily.length) {
    const count = afterFamily.length - beforeFamily.length;
    details.push(`${count} family circle member${count === 1 ? "" : "s"} added`);
  }
  if (afterFamily.length < beforeFamily.length) {
    const count = beforeFamily.length - afterFamily.length;
    details.push(`${count} family circle member${count === 1 ? "" : "s"} removed`);
  }

  details.push(
    ...diffStringList(
      before.buyOutInfo?.terms,
      after.buyOutInfo?.terms,
      "buy-out term",
      "buy-out terms"
    )
  );

  if (changed(before.cheatSheet?.nuggets, after.cheatSheet?.nuggets)) {
    details.push("Cheat sheet nuggets were updated");
  }
  if (changed(before.cheatSheet?.playerContact?.phone, after.cheatSheet?.playerContact?.phone)) {
    details.push("Cheat sheet player phone was updated");
  }
  if (changed(before.cheatSheet?.playerContact?.email, after.cheatSheet?.playerContact?.email)) {
    details.push("Cheat sheet player email was updated");
  }

  if (changed(before.socialMedia?.twitter?.account, after.socialMedia?.twitter?.account)) {
    details.push("Twitter account was updated");
  }
  if (changed(before.socialMedia?.instagram?.account, after.socialMedia?.instagram?.account)) {
    details.push("Instagram account was updated");
  }

  details.push(
    ...diffStringList(
      before.socialMedia?.twitter?.nuggets,
      after.socialMedia?.twitter?.nuggets,
      "Twitter nugget",
      "Twitter nuggets"
    )
  );
  details.push(
    ...diffStringList(
      before.socialMedia?.instagram?.nuggets,
      after.socialMedia?.instagram?.nuggets,
      "Instagram nugget",
      "Instagram nuggets"
    )
  );

  const beforeAwards = (before.awards || []).filter(isNonEmptyObject);
  const afterAwards = (after.awards || []).filter(isNonEmptyObject);
  if (afterAwards.length > beforeAwards.length) {
    const count = afterAwards.length - beforeAwards.length;
    details.push(`${count} award${count === 1 ? "" : "s"} added`);
  }

  return details;
}

export function describeDeletedBackgroundOverview() {
  return ["Background overview was deleted"];
}

function normalizeReportSnapshot(report = {}) {
  return {
    type: report.type || "",
    reportDate: report.reportDate || "",
    reportPreview: report.reportPreview || "",
    location: report.location || "",
    reportContent: report.reportContent || "",
    drafted: report.drafted,
    nbaDraft: report.nbaDraft || "",
    nbaCeiling: report.nbaCeiling || "",
    attachment: report.attachment || "",
    typeSpecificFields: report.typeSpecificFields || {},
  };
}

export function describeNewReport(values = {}) {
  const type = values.type || "Report";
  const details = [`New ${type} report was added`];

  if (norm(values.reportDate)) details.push("Report date was set");
  if (norm(values.location)) details.push("Report location was set");
  if (norm(values.reportContent)) details.push("Report content was added");

  const ts = values.typeSpecificFields || {};
  if (type === "Scouting") {
    const roles = (ts.roles || []).filter((role) => norm(role));
    if (roles.length) {
      details.push(`${roles.length} scouting role${roles.length === 1 ? "" : "s"} added`);
    }
    if ((ts.stats || []).length) details.push("Scouting stats were added");
  }
  if (type === "Intel" && norm(ts.source)) details.push("Intel source was set");
  if (["General", "Medical"].includes(type) && norm(ts.urlReference)) {
    details.push("URL reference was set");
  }

  return details;
}

export function diffReport(before = {}, after = {}) {
  const prev = normalizeReportSnapshot(before);
  const next = normalizeReportSnapshot(after);
  const details = [];
  const typeLabel = next.type || prev.type || "Report";

  if (changed(prev.type, next.type)) details.push(`Report type was updated to ${next.type}`);
  if (changed(prev.reportDate, next.reportDate)) details.push("Report date was updated");
  if (changed(prev.location, next.location)) details.push("Report location was updated");
  if (changed(prev.reportPreview, next.reportPreview)) details.push("Report preview was updated");
  if (changed(prev.reportContent, next.reportContent)) details.push("Report content was updated");
  if (changed(prev.drafted, next.drafted)) details.push("Drafted status was updated");
  if (changed(prev.nbaDraft, next.nbaDraft)) details.push("NBA draft info was updated");
  if (changed(prev.nbaCeiling, next.nbaCeiling)) details.push("NBA ceiling was updated");
  if (changed(prev.attachment, next.attachment)) details.push("Report attachment was updated");

  const prevTs = prev.typeSpecificFields || {};
  const nextTs = next.typeSpecificFields || {};

  if (["General", "Medical"].includes(next.type)) {
    if (changed(prevTs.urlReference, nextTs.urlReference)) details.push("URL reference was updated");
    if (changed(prevTs.source, nextTs.source)) details.push("Report source was updated");
  }
  if (next.type === "Intel" && changed(prevTs.source, nextTs.source)) {
    details.push("Intel source was updated");
  }
  if (next.type === "Scouting") {
    const prevRoles = (prevTs.roles || []).filter((role) => norm(role));
    const nextRoles = (nextTs.roles || []).filter((role) => norm(role));
    details.push(...diffStringList(prevRoles, nextRoles, "scouting role", "scouting roles"));
    if (changed(prevTs.projectionRange, nextTs.projectionRange)) {
      details.push("Projection range was updated");
    }
    if (changed(prevTs.stats, nextTs.stats)) details.push("Scouting stats were updated");
  }

  return details.length ? details : [`${typeLabel} report was edited`];
}

export function describeDeletedReport(report = {}) {
  const type = report.type || "Report";
  return [`${type} report was deleted`];
}
